import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ChevronUp,
  ChevronDown,
  MoreHorizontal,
  Edit2,
  Archive,
  CheckCircle,
} from 'lucide-react';
import { Search, MusicIcon, VisualArtIcon, WritingIcon, VideoIcon } from '../../icons';
import { ProjectDto } from '../../../services';
import './PieceList.css';

interface PieceListProps {
  pieces: ProjectDto[];
  isLoading?: boolean;
  onPieceClick?: (piece: ProjectDto) => void;
  onEditPiece?: (piece: ProjectDto) => void;
  onArchivePiece?: (piece: ProjectDto) => void;
  onCompletePiece?: (piece: ProjectDto) => void;
}

type SortField = 'title' | 'projectType' | 'currentStage' | 'status' | 'updatedAt';
type SortDirection = 'asc' | 'desc';

const disciplineIcons: Record<string, React.ReactNode> = {
  Music: <MusicIcon size={16} />,
  Art: <VisualArtIcon size={16} />,
  Writing: <WritingIcon size={16} />,
  Video: <VideoIcon size={16} />,
};

const formatRelativeTime = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

export const PieceList: React.FC<PieceListProps> = ({
  pieces,
  isLoading = false,
  onPieceClick,
  onEditPiece,
  onArchivePiece,
  onCompletePiece,
}) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [disciplineFilter, setDisciplineFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sortField, setSortField] = useState<SortField>('updatedAt');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);

  // Get unique disciplines and statuses for filters
  const disciplines = useMemo(() => {
    const types = new Set(pieces.map(p => p.projectType));
    return Array.from(types);
  }, [pieces]);

  const statuses = useMemo(() => {
    const statusSet = new Set(pieces.map(p => p.status));
    return Array.from(statusSet);
  }, [pieces]);

  // Filter and sort pieces
  const filteredPieces = useMemo(() => {
    let result = [...pieces];

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(p => p.title.toLowerCase().includes(query));
    }

    // Discipline filter
    if (disciplineFilter !== 'all') {
      result = result.filter(p => p.projectType === disciplineFilter);
    }

    // Status filter
    if (statusFilter !== 'all') {
      result = result.filter(p => p.status === statusFilter);
    }

    // Sort
    result.sort((a, b) => {
      let aVal: string | Date;
      let bVal: string | Date;

      switch (sortField) {
        case 'title':
          aVal = a.title.toLowerCase();
          bVal = b.title.toLowerCase();
          break;
        case 'projectType':
          aVal = a.projectType;
          bVal = b.projectType;
          break;
        case 'currentStage':
          aVal = a.currentStage;
          bVal = b.currentStage;
          break;
        case 'status':
          aVal = a.status;
          bVal = b.status;
          break;
        case 'updatedAt':
          aVal = new Date(a.updatedAt || a.createdAt);
          bVal = new Date(b.updatedAt || b.createdAt);
          break;
        default:
          return 0;
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [pieces, searchQuery, disciplineFilter, statusFilter, sortField, sortDirection]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const renderSortIcon = (field: SortField) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />;
  };

  const handleMenuToggle = (pieceId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setOpenMenuId(openMenuId === pieceId ? null : pieceId);
  };

  const handleAction = (action: (piece: ProjectDto) => void, piece: ProjectDto, e: React.MouseEvent) => {
    e.stopPropagation();
    setOpenMenuId(null);
    action(piece);
  };

  if (isLoading) {
    return (
      <div className="piece-list piece-list--loading">
        <div className="piece-list__skeleton">
          <div className="piece-list__skeleton-controls" />
          <div className="piece-list__skeleton-header" />
          {[1, 2, 3, 4, 5].map(i => (
            <div key={i} className="piece-list__skeleton-row" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="piece-list">
      {/* Filter Controls */}
      <div className="piece-list__controls">
        <div className="piece-list__search">
          <Search size={16} />
          <input
            type="text"
            placeholder={t('opusDashboard.searchPieces', 'Search pieces...')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="piece-list__search-input"
            aria-label={t('opusDashboard.searchPieces', 'Search pieces')}
          />
        </div>

        <div className="piece-list__filters">
          <select
            value={disciplineFilter}
            onChange={(e) => setDisciplineFilter(e.target.value)}
            className="piece-list__filter-select"
            aria-label={t('opusDashboard.filterByDiscipline', 'Filter by discipline')}
          >
            <option value="all">{t('opusDashboard.allDisciplines', 'All Disciplines')}</option>
            {disciplines.map(discipline => (
              <option key={discipline} value={discipline}>
                {discipline}
              </option>
            ))}
          </select>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="piece-list__filter-select"
            aria-label={t('opusDashboard.filterByStatus', 'Filter by status')}
          >
            <option value="all">{t('opusDashboard.allStatuses', 'All Statuses')}</option>
            {statuses.map(status => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </select>
        </div>

        <div className="piece-list__summary">
          <span className="piece-list__summary-count">{filteredPieces.length}</span>
          <span className="piece-list__summary-label">
            {filteredPieces.length === 1
              ? t('opusDashboard.piece', 'Piece')
              : t('opusDashboard.pieces', 'Pieces')}
          </span>
        </div>
      </div>

      {/* Table */}
      {filteredPieces.length === 0 ? (
        <div className="piece-list__empty">
          <p>{searchQuery || disciplineFilter !== 'all' || statusFilter !== 'all'
            ? t('opusDashboard.noMatchingPieces', 'No pieces match your filters')
            : t('opusDashboard.noPiecesYet', 'No pieces yet')}
          </p>
        </div>
      ) : (
        <div className="piece-list__table-container">
          <table className="piece-list__table">
            <thead>
              <tr>
                <th
                  className="piece-list__th piece-list__th--sortable"
                  onClick={() => handleSort('title')}
                >
                  {t('opusDashboard.title', 'Title')}
                  {renderSortIcon('title')}
                </th>
                <th
                  className="piece-list__th piece-list__th--sortable"
                  onClick={() => handleSort('projectType')}
                >
                  {t('opusDashboard.discipline', 'Discipline')}
                  {renderSortIcon('projectType')}
                </th>
                <th
                  className="piece-list__th piece-list__th--sortable"
                  onClick={() => handleSort('currentStage')}
                >
                  {t('opusDashboard.stage', 'Stage')}
                  {renderSortIcon('currentStage')}
                </th>
                <th
                  className="piece-list__th piece-list__th--sortable"
                  onClick={() => handleSort('status')}
                >
                  {t('opusDashboard.status', 'Status')}
                  {renderSortIcon('status')}
                </th>
                <th
                  className="piece-list__th piece-list__th--sortable"
                  onClick={() => handleSort('updatedAt')}
                >
                  {t('opusDashboard.lastUpdated', 'Last Updated')}
                  {renderSortIcon('updatedAt')}
                </th>
                <th className="piece-list__th piece-list__th--actions">
                  {t('opusDashboard.actions', 'Actions')}
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredPieces.map((piece) => (
                <tr
                  key={piece.id}
                  className={`piece-list__row ${piece.status === 'Completed' ? 'piece-list__row--completed' : ''}`}
                  onClick={() => onPieceClick?.(piece)}
                >
                  <td className="piece-list__td piece-list__td--title">
                    <button className="piece-list__title-btn">
                      {piece.title}
                    </button>
                  </td>
                  <td className="piece-list__td piece-list__td--discipline">
                    <span className="piece-list__discipline">
                      {disciplineIcons[piece.projectType] || <MusicIcon size={16} />}
                      {piece.projectType}
                    </span>
                  </td>
                  <td className="piece-list__td">
                    <span className="piece-list__stage">{piece.currentStage}</span>
                  </td>
                  <td className="piece-list__td">
                    <span className={`piece-list__status piece-list__status--${piece.status.toLowerCase()}`}>
                      {piece.status}
                    </span>
                  </td>
                  <td className="piece-list__td piece-list__td--time">
                    {formatRelativeTime(piece.updatedAt || piece.createdAt)}
                  </td>
                  <td className="piece-list__td piece-list__td--actions">
                    <div className="piece-list__actions">
                      <button
                        className="piece-list__action-btn"
                        onClick={(e) => handleMenuToggle(piece.id, e)}
                        aria-label={t('opusDashboard.moreActions', 'More actions')}
                        aria-expanded={openMenuId === piece.id}
                      >
                        <MoreHorizontal size={16} />
                      </button>
                      {openMenuId === piece.id && (
                        <div className="piece-list__menu">
                          {onEditPiece && (
                            <button
                              className="piece-list__menu-item"
                              onClick={(e) => handleAction(onEditPiece, piece, e)}
                            >
                              <Edit2 size={14} />
                              {t('opusDashboard.edit', 'Edit')}
                            </button>
                          )}
                          {onCompletePiece && piece.status !== 'Completed' && (
                            <button
                              className="piece-list__menu-item"
                              onClick={(e) => handleAction(onCompletePiece, piece, e)}
                            >
                              <CheckCircle size={14} />
                              {t('opusDashboard.markComplete', 'Mark Complete')}
                            </button>
                          )}
                          {onArchivePiece && (
                            <button
                              className="piece-list__menu-item piece-list__menu-item--danger"
                              onClick={(e) => handleAction(onArchivePiece, piece, e)}
                            >
                              <Archive size={14} />
                              {t('opusDashboard.archive', 'Archive')}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Mobile Card View (hidden on desktop, shown on mobile via CSS) */}
      <div className="piece-list__mobile-cards">
        {filteredPieces.map((piece) => (
          <button
            key={piece.id}
            className={`piece-list__mobile-card ${piece.status === 'Completed' ? 'piece-list__mobile-card--completed' : ''}`}
            onClick={() => onPieceClick?.(piece)}
          >
            <div className="piece-list__mobile-card-header">
              <span className="piece-list__mobile-card-discipline">
                {disciplineIcons[piece.projectType] || <MusicIcon size={16} />}
              </span>
              <span className={`piece-list__status piece-list__status--${piece.status.toLowerCase()}`}>
                {piece.status}
              </span>
            </div>
            <h4 className="piece-list__mobile-card-title">{piece.title}</h4>
            <div className="piece-list__mobile-card-meta">
              <span>{piece.currentStage}</span>
              <span>{formatRelativeTime(piece.updatedAt || piece.createdAt)}</span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

export default PieceList;
